import React from "react";
import {
  IconBox,
  IconArrowUpRight,
  IconArrowDownLeft,
  IconShoppingCart,
  IconAlertTriangle,
  IconRotate,
  IconUsers,
  IconFlame,
  IconFileText,
  IconCheck,
} from "@tabler/icons-react";
import { iconStroke } from "../../config/config";

export default function DashboardView({ stats, isLoading, onSelectTab }) {
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center p-12 text-gray-400">
        <IconRotate className="animate-spin mb-3" size={36} stroke={iconStroke} />
        <p className="text-sm">Loading custody dashboard...</p>
      </div>
    );
  }

  const {
    active_issues_count = 0,
    outstanding_custody_base_qty = 0,
    pending_sales_base_qty = 0,
    today_movements_count = 0,
    pending_reconciliations_count = 0,
    discrepancies_count = 0,
    top_custodians = [],
    recent_movements = [],
  } = stats || {};

  return (
    <div className="space-y-6">
      {/* Quick Action Bar */}
      <div className="flex flex-wrap items-center justify-between gap-3 bg-base-100 p-4 rounded-2xl border border-base-200 shadow-sm">
        <div>
          <h2 className="text-lg font-bold text-base-content">Item Issue & Custody Overview</h2>
          <p className="text-xs text-base-content/60">
            Track employee custody, POS sales attribution, returns, and physical stock reconciliations in real time.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => onSelectTab("issue")}
            className="btn btn-sm btn-primary gap-1 shadow-sm"
          >
            <IconArrowUpRight size={16} stroke={iconStroke} />
            Issue Stock
          </button>
          <button
            onClick={() => onSelectTab("return")}
            className="btn btn-sm btn-outline btn-success gap-1 shadow-sm"
          >
            <IconArrowDownLeft size={16} stroke={iconStroke} />
            Return Stock
          </button>
          <button
            onClick={() => onSelectTab("consumption")}
            className="btn btn-sm btn-outline btn-warning gap-1 shadow-sm"
          >
            <IconFlame size={16} stroke={iconStroke} />
            Non-Sale Usage
          </button>
          <button
            onClick={() => onSelectTab("reconciliation")}
            className="btn btn-sm btn-outline btn-secondary gap-1 shadow-sm"
          >
            <IconCheck size={16} stroke={iconStroke} />
            Audit & Reconcile
          </button>
        </div>
      </div>

      {/* KPI Cards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Active Issues */}
        <div className="bg-base-100 p-5 rounded-2xl border border-base-200 shadow-sm hover:shadow transition-shadow">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-base-content/60 uppercase tracking-wider">
              Active Issues
            </span>
            <div className="p-2.5 rounded-xl bg-primary/10 text-primary">
              <IconBox size={22} stroke={iconStroke} />
            </div>
          </div>
          <div className="mt-3">
            <span className="text-2xl font-black text-base-content">{active_issues_count}</span>
            <p className="text-xs text-base-content/60 mt-1">Open custody allocations</p>
          </div>
        </div>

        {/* Outstanding Custody */}
        <div className="bg-base-100 p-5 rounded-2xl border border-base-200 shadow-sm hover:shadow transition-shadow">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-base-content/60 uppercase tracking-wider">
              Outstanding Custody
            </span>
            <div className="p-2.5 rounded-xl bg-info/10 text-info">
              <IconUsers size={22} stroke={iconStroke} />
            </div>
          </div>
          <div className="mt-3">
            <span className="text-2xl font-black text-base-content">
              {Number(outstanding_custody_base_qty).toLocaleString(undefined, {
                maximumFractionDigits: 2,
              })}
            </span>
            <p className="text-xs text-base-content/60 mt-1">Base units currently with staff</p>
          </div>
        </div>

        {/* Pending POS Sales */}
        <div className="bg-base-100 p-5 rounded-2xl border border-base-200 shadow-sm hover:shadow transition-shadow">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-base-content/60 uppercase tracking-wider">
              Pending POS Sales
            </span>
            <div className="p-2.5 rounded-xl bg-warning/10 text-warning">
              <IconShoppingCart size={22} stroke={iconStroke} />
            </div>
          </div>
          <div className="mt-3">
            <span className="text-2xl font-black text-base-content">
              {Number(pending_sales_base_qty).toLocaleString(undefined, {
                maximumFractionDigits: 2,
              })}
            </span>
            <p className="text-xs text-base-content/60 mt-1">Ordered but unsettled bills</p>
          </div>
        </div>

        {/* Discrepancies & Audits */}
        <div className="bg-base-100 p-5 rounded-2xl border border-base-200 shadow-sm hover:shadow transition-shadow">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-base-content/60 uppercase tracking-wider">
              Discrepancies
            </span>
            <div
              className={`p-2.5 rounded-xl ${
                discrepancies_count > 0 ? "bg-error/10 text-error" : "bg-success/10 text-success"
              }`}
            >
              <IconAlertTriangle size={22} stroke={iconStroke} />
            </div>
          </div>
          <div className="mt-3">
            <span className="text-2xl font-black text-base-content">{discrepancies_count}</span>
            <p className="text-xs text-base-content/60 mt-1">
              {pending_reconciliations_count} pending approvals
            </p>
          </div>
        </div>
      </div>

      {/* Main Content Two-Column Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Top Custodians */}
        <div className="lg:col-span-1 bg-base-100 p-5 rounded-2xl border border-base-200 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-base-content flex items-center gap-2">
              <IconUsers size={18} stroke={iconStroke} className="text-primary" />
              Active Custodians
            </h3>
            <button
              onClick={() => onSelectTab("custody")}
              className="text-xs font-medium text-primary hover:underline"
            >
              View All
            </button>
          </div>

          {top_custodians.length === 0 ? (
            <div className="text-center py-8 text-xs text-base-content/60">
              No staff currently holding stock custody.
            </div>
          ) : (
            <div className="space-y-3">
              {top_custodians.map((cust, idx) => (
                <div
                  key={idx}
                  className="flex items-center justify-between p-3 rounded-xl bg-base-200/50 hover:bg-base-200 transition-colors"
                >
                  <div className="min-w-0 pr-2">
                    <p className="font-semibold text-xs text-base-content truncate">
                      {cust.employee_name || cust.user_id}
                    </p>
                    <p className="text-[11px] text-base-content/60 truncate">
                      {cust.department || "General Staff"} • {cust.active_items_count} items
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <span className="badge badge-sm badge-info font-mono">
                      {Number(cust.total_custody_base_qty).toLocaleString()} base
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent Movements Audit */}
        <div className="lg:col-span-2 bg-base-100 p-5 rounded-2xl border border-base-200 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-base-content flex items-center gap-2">
              <IconFileText size={18} stroke={iconStroke} className="text-primary" />
              Recent Movements Ledger
            </h3>
            <button
              onClick={() => onSelectTab("movements")}
              className="text-xs font-medium text-primary hover:underline"
            >
              Full Ledger
            </button>
          </div>

          {recent_movements.length === 0 ? (
            <div className="text-center py-8 text-xs text-base-content/60">
              No stock movements recorded yet.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="table table-xs w-full">
                <thead>
                  <tr className="text-base-content/60">
                    <th>Time</th>
                    <th>Type</th>
                    <th>Product</th>
                    <th>Custodian</th>
                    <th className="text-right">Qty</th>
                    <th className="text-right">Base Qty</th>
                  </tr>
                </thead>
                <tbody>
                  {recent_movements.slice(0, 7).map((m, idx) => {
                    const badgeClass =
                      m.movement_type === "ISSUE"
                        ? "badge-primary"
                        : m.movement_type === "RETURN"
                        ? "badge-success"
                        : m.movement_type === "SETTLED_SALE"
                        ? "badge-info"
                        : m.movement_type === "CONSUMPTION"
                        ? "badge-warning"
                        : "badge-secondary";

                    return (
                      <tr key={idx} className="hover:bg-base-200/40">
                        <td className="text-[11px] text-base-content/60 whitespace-nowrap">
                          {new Date(m.created_at).toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </td>
                        <td>
                          <span className={`badge badge-xs ${badgeClass} font-semibold`}>
                            {m.movement_type}
                          </span>
                        </td>
                        <td className="font-medium text-xs truncate max-w-[140px]">
                          {m.item_title}
                        </td>
                        <td className="text-xs text-base-content/70 truncate max-w-[120px]">
                          {m.employee_name || m.user_id}
                        </td>
                        <td className="text-right text-xs whitespace-nowrap">
                          {m.quantity} {m.unit_name}
                        </td>
                        <td className="text-right font-mono font-bold text-xs text-primary whitespace-nowrap">
                          {Number(m.base_quantity).toLocaleString()} {m.base_unit}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
